// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { apiurl } from "utils";
import Select2Field from "components/Select2Field";
import Loading from "components/Loading";
import TablePaginationBar from "components/TablePaginationBar";
import debounce from "debounce";
import Swal from "sweetalert2";
import { useTranslate } from "utils/i18n";
import { useRegisterAssistantPage } from "assistant/useRegisterAssistantPage";
import { parseListPayload, totalPagesFromCount } from "utils/paginationStats";
import {
  EMPTY_ASSIGNMENT_FILTERS,
  fetchAssignmentList,
} from "services/assignmentListService";
import {
  EMPTY_ESTABLISHMENT_FILTERS,
  buildAssignmentCreationEntries,
  buildAssignmentEstablishmentParams,
  deriveEstablishmentFilterValues,
  establishmentStatusOptions,
  establishmentTypeOptions,
  filterEstablishmentRows,
} from "services/establishmentFilters";
import useEstablishmentFilterOptions from "services/useEstablishmentFilterOptions";
import {
  ASSIGNMENT_TEACHER_PAGE_SIZE,
  fetchAssignmentTeachers,
} from "services/assignmentTeacherService";
import {
  figmaTokens,
  directoryTableHeaderCellSx,
  directoryTableHeadStickyOverridesSx,
  directoryTableBodyCellDefaultsSx,
} from "assets/theme/designTokens";
import { useSoftUIController } from "context";
import { isObserver } from "assistant/userRole";
import { teacherInitials } from "utils/teacherAvatar";

const GEO_API = {
  provinces: `${apiurl}Assignment/provinces`,
  departments: (provid) => `${apiurl}Assignment/departments?ProvId=${provid}`,
  subprefectures: (dptid) => `${apiurl}Assignment/subprefectures?DptId=${dptid}`,
  establishments: `${apiurl}Assignment/establishments`,
};

const swalApp = {
  confirmButtonColor: figmaTokens.accent,
  background: figmaTokens.surface,
  color: figmaTokens.textPrimary,
};

const filterLabelSx = {
  display: "block",
  mb: 0.5,
  fontWeight: 700,
  fontSize: "0.72rem",
  letterSpacing: "0.04em",
  color: figmaTokens.textSecondary,
  textTransform: "uppercase",
};

const makeOptions = (data, ids, labels) =>
  (Array.isArray(data) ? data : []).map((item) => ({
    id: ids.map((key) => item[key]).find(Boolean),
    text: labels.map((key) => item[key]).find(Boolean),
  }));

function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem("tk446655440000")}` };
}

function pickRow(row, ...keys) {
  if (!row) return "";
  for (const key of keys) {
    const v = row[key];
    if (v !== null && v !== undefined && String(v).trim() !== "") return v;
  }
  return "";
}

function formatAssignmentDate(value) {
  if (!value) return "--";
  return String(value).replace("T", " ").slice(0, 16);
}

function assignmentTeacherStatus(row) {
  return String(pickRow(row, "Status", "status", "TeacherStatus", "teacherStatus")).trim();
}

function assignmentStatusChipSx(raw) {
  const s = String(raw ?? "").trim().toLowerCase();
  const base = {
    fontWeight: 700,
    fontSize: "0.72rem",
    height: 28,
    minHeight: 28,
    borderRadius: "999px",
    "& .MuiChip-label": { px: 1.35 },
  };
  if (!s) {
    return {
      ...base,
      bgcolor: figmaTokens.surface,
      color: figmaTokens.textMuted,
      border: `1px solid ${figmaTokens.borderStrong}`,
      boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
    };
  }
  if (s.includes("inact") || s.includes("suspend") || s.includes("révoq") || s.includes("revoq")) {
    return {
      ...base,
      color: "#fff",
      bgcolor: "#dc2626",
      border: "none",
      boxShadow: "0 4px 12px rgba(220, 38, 38, 0.38)",
    };
  }
  if (s.includes("act") || s.includes("valid") || s === "ok" || s.includes("en poste")) {
    return {
      ...base,
      color: "#fff",
      background: figmaTokens.gradientCSS,
      border: "none",
      boxShadow: "0 4px 14px rgba(102, 126, 234, 0.42)",
    };
  }
  return {
    ...base,
    bgcolor: figmaTokens.pageBg,
    color: figmaTokens.textSecondary,
    border: `1px solid ${figmaTokens.borderStrong}`,
    fontWeight: 600,
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
  };
}

const assignmentStatWrapSx = {
  minWidth: 0,
  height: "100%",
  display: "flex",
  "& > .MuiCard-root": {
    flex: 1,
    minHeight: 108,
    borderRadius: "16px",
    border: `1px solid ${figmaTokens.borderSubtle}`,
    boxShadow: "0 10px 34px rgba(15, 23, 42, 0.07)",
    backgroundColor: figmaTokens.surface,
    backgroundImage: `linear-gradient(145deg, rgba(118, 75, 162, 0.08) 0%, ${figmaTokens.surface} 52%)`,
    overflow: "hidden",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    "&:hover": {
      borderColor: "rgba(118, 75, 162, 0.28)",
      boxShadow: "0 16px 42px rgba(118, 75, 162, 0.12)",
      transform: "translateY(-2px)",
    },
    "& > div": {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
  },
};

const assignmentTableShellSx = {
  mx: { xs: 1.25, md: 2 },
  mb: 0,
  borderRadius: "14px",
  border: `1px solid ${figmaTokens.borderSubtle}`,
  overflow: "auto",
  maxHeight: { xs: "65vh", md: "min(68vh, 680px)" },
  bgcolor: figmaTokens.surface,
  boxShadow: "0 10px 40px rgba(15, 23, 42, 0.07)",
};

function Affectations() {
  const [controller] = useSoftUIController();
  const readOnly = isObserver(controller.user);
  const [provOpts, setProvOpts] = useState([]);
  const [deptOpts, setDeptOpts] = useState([]);
  const [subpOpts, setSubpOpts] = useState([]);
  const [estabOpts, setEstabOpts] = useState([]);
  const [filterDeptOpts, setFilterDeptOpts] = useState([]);
  const [filterSubpOpts, setFilterSubpOpts] = useState([]);
  const [filterEstabOpts, setFilterEstabOpts] = useState([]);
  const [teacherOpts, setTeacherOpts] = useState([]);
  const [teacherAssignmentFilter, setTeacherAssignmentFilter] = useState("all");
  const teacherOptionCacheRef = useRef(new Map());
  const [loading, setLoading] = useState(false);
  const [assignmentSummary, setAssignmentSummary] = useState([]);
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [assignmentApiSummary, setAssignmentApiSummary] = useState({
    totalRecords: 0,
    thisMonthTotal: 0,
    todayTotal: 0,
    thisWeekTotal: 0,
    totalEstablishments: 0,
  });
  const [pageSize, setPageSize] = useState(20);
  const [formOpen, setFormOpen] = useState(false);
  const [assignmentEstablishmentRows, setAssignmentEstablishmentRows] = useState([]);
  const [draftListFilters, setDraftListFilters] = useState(EMPTY_ASSIGNMENT_FILTERS);
  const [appliedListFilters, setAppliedListFilters] = useState(EMPTY_ASSIGNMENT_FILTERS);
  const [activeApiFilters, setActiveApiFilters] = useState(null);
  const [filtersMinimized, setFiltersMinimized] = useState(false);
  const { t } = useTranslate();
  const {
    typeOptions: establishmentTypeFilterOptions,
    statusOptions: establishmentStatusFilterOptions,
  } = useEstablishmentFilterOptions({
    t,
    provinceId: draftListFilters.provinceId,
    departmentId: draftListFilters.departmentId,
    subPrefectureId: draftListFilters.subPrefectureId,
  });

  const cache = useRef({
    provinces: null,
    departments: {},
    subprefectures: {},
    establishments: {},
    filterDepartments: {},
    filterSubprefectures: {},
    filterEstablishments: {},
  });

  const controllers = useRef({});

  const cancelRequest = (key) => {
    if (controllers.current[key]) controllers.current[key].abort();
    controllers.current[key] = new AbortController();
    return controllers.current[key].signal;
  };

  const loadProvinces = useCallback(async () => {
    if (cache.current.provinces) return cache.current.provinces;
    const { data } = await axios.get(GEO_API.provinces, { headers: authHeader() });
    const opts = makeOptions(data, ["provid", "id", "ProvId"], ["name", "province", "Name"]);
    cache.current.provinces = opts;
    return opts;
  }, []);

  const fetchProvinces = useCallback(async () => {
    try {
      const opts = await loadProvinces();
      setProvOpts(opts);
    } catch (err) {
      console.error("Fetch provinces error", err);
    }
  }, [loadProvinces]);

  const loadDepartments = useCallback(
    async (provid, cacheBucket) => {
      if (!provid) return [];
      if (cacheBucket[provid]) return cacheBucket[provid];
      const { data } = await axios.get(GEO_API.departments(provid), {
        signal: cancelRequest(`departments-${provid}`),
        headers: authHeader(),
      });
      const opts = makeOptions(data, ["dptid", "id", "DptId"], ["name", "department", "Name"]);
      cacheBucket[provid] = opts;
      return opts;
    },
    []
  );

  const loadSubprefectures = useCallback(async (dptid, cacheBucket) => {
    if (!dptid) return [];
    if (cacheBucket[dptid]) return cacheBucket[dptid];
    const { data } = await axios.get(GEO_API.subprefectures(dptid), {
      signal: cancelRequest(`subprefectures-${dptid}`),
      headers: authHeader(),
    });
    const opts = makeOptions(data, ["sptid", "id", "SptId"], ["name", "subprefecture", "Name"]);
    cacheBucket[dptid] = opts;
    return opts;
  }, []);

  const loadEstablishments = useCallback(async (
    sptid,
    cacheBucket,
    filters = {},
    requestScope = "establishments"
  ) => {
    if (!sptid) return [];
    const params = buildAssignmentEstablishmentParams(sptid, filters);
    const cacheKey = JSON.stringify(params);
    if (cacheBucket[cacheKey]) return cacheBucket[cacheKey];
    const { data } = await axios.get(GEO_API.establishments, {
      params,
      signal: cancelRequest(requestScope),
      headers: authHeader(),
    });
    const opts = makeOptions(
      parseListPayload(data),
      ["id", "Id", "estabId", "EstabId", "establishmentId", "EstablishmentId"],
      ["name", "establishment", "Name", "EstablishmentName"]
    );
    cacheBucket[cacheKey] = opts;
    return opts;
  }, []);

  const fetchDepartments = useCallback(
    debounce(async (provid) => {
      if (!provid) return setDeptOpts([]);
      try {
        const opts = await loadDepartments(provid, cache.current.departments);
        setDeptOpts(opts);
      } catch (err) {
        if (err.name !== "CanceledError") console.error("Fetch departments error", err);
      }
    }, 300),
    [loadDepartments]
  );

  const fetchSubprefectures = useCallback(
    debounce(async (dptid) => {
      if (!dptid) return setSubpOpts([]);
      try {
        const opts = await loadSubprefectures(dptid, cache.current.subprefectures);
        setSubpOpts(opts);
      } catch (err) {
        if (err.name !== "CanceledError") console.error("Fetch subprefectures error", err);
      }
    }, 300),
    [loadSubprefectures]
  );

  const fetchEstablishments = useCallback(
    debounce(async (sptid) => {
      if (!sptid) {
        setAssignmentEstablishmentRows([]);
        return setEstabOpts([]);
      }
      try {
        const { data } = await axios.get(GEO_API.establishments, {
          params: buildAssignmentEstablishmentParams(sptid),
          signal: cancelRequest("assignment-form-establishments"),
          headers: authHeader(),
        });
        const rows = parseListPayload(data);
        setAssignmentEstablishmentRows(rows);
        setEstabOpts(
          makeOptions(
            rows,
            ["id", "Id", "estabId", "EstabId", "establishmentId", "EstablishmentId"],
            ["name", "establishment", "Name", "EstablishmentName"]
          )
        );
      } catch (err) {
        if (err.name !== "CanceledError") console.error("Fetch establishments error", err);
      }
    }, 300),
    []
  );

  const fetchFilterDepartments = useCallback(
    debounce(async (provid) => {
      if (!provid) return setFilterDeptOpts([]);
      try {
        const opts = await loadDepartments(provid, cache.current.filterDepartments);
        setFilterDeptOpts(opts);
      } catch (err) {
        if (err.name !== "CanceledError") console.error("Fetch filter departments error", err);
      }
    }, 300),
    [loadDepartments]
  );

  const fetchFilterSubprefectures = useCallback(
    debounce(async (dptid) => {
      if (!dptid) return setFilterSubpOpts([]);
      try {
        const opts = await loadSubprefectures(dptid, cache.current.filterSubprefectures);
        setFilterSubpOpts(opts);
      } catch (err) {
        if (err.name !== "CanceledError") console.error("Fetch filter subprefectures error", err);
      }
    }, 300),
    [loadSubprefectures]
  );

  const fetchFilterEstablishments = useCallback(
    debounce(async (sptid, filters = EMPTY_ESTABLISHMENT_FILTERS) => {
      if (!sptid) return setFilterEstabOpts([]);
      try {
        const opts = await loadEstablishments(
          sptid,
          cache.current.filterEstablishments,
          filters,
          "assignment-list-filter-establishments"
        );
        setFilterEstabOpts(opts);
      } catch (err) {
        if (err.name !== "CanceledError") console.error("Fetch filter establishments error", err);
      }
    }, 300),
    [loadEstablishments]
  );

  const loadTeacherOptions = useCallback(
    async (request) => {
      const result = await fetchAssignmentTeachers({
        ...request,
        assignmentStatus: teacherAssignmentFilter,
      });
      result.items.forEach((option) => {
        teacherOptionCacheRef.current.set(String(option.id), option);
      });
      return result;
    },
    [teacherAssignmentFilter]
  );

  const fetchAssignmentSummary = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchAssignmentList({
        pageNumber: page,
        pageSize,
        filters: appliedListFilters,
      });
      setAssignmentSummary(result.rows);
      setAssignmentApiSummary(result.stats);
      setTotalRecords(result.stats.totalRecords || result.rows.length);
      setActiveApiFilters(result.activeFilters);
    } catch (err) {
      console.error("API Error:", err);
      setAssignmentSummary([]);
      setAssignmentApiSummary({
        totalRecords: 0,
        thisMonthTotal: 0,
        todayTotal: 0,
        thisWeekTotal: 0,
        totalEstablishments: 0,
      });
      setTotalRecords(0);
      setActiveApiFilters(null);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedListFilters]);

  useEffect(() => {
    fetchProvinces();
  }, [fetchProvinces]);

  useEffect(() => {
    fetchAssignmentSummary();
  }, [fetchAssignmentSummary]);

  const findExistingTeacherAssignment = useCallback(
    async (teacherId) => {
      const selectedTeacher = teacherOpts.find(
        (teacher) => String(teacher.id) === String(teacherId)
      );
      if (!selectedTeacher) return null;

      if (selectedTeacher.currentEstablishmentId || selectedTeacher.currentEstablishmentName) {
        return {
          establishmentId: selectedTeacher.currentEstablishmentId,
          establishmentName: selectedTeacher.currentEstablishmentName,
          teacherName: [selectedTeacher.name, selectedTeacher.preName].filter(Boolean).join(" "),
        };
      }

      if (!selectedTeacher.regNumber) return null;

      try {
        const result = await fetchAssignmentList({
          pageNumber: 1,
          pageSize: 10,
          filters: {
            ...EMPTY_ASSIGNMENT_FILTERS,
            regNumber: selectedTeacher.regNumber,
          },
        });
        const exactMatch = result.rows.find((row) => {
          const rowTeacherId = pickRow(row, "AsignId", "asignId", "TeacherId", "teacherId");
          const rowRegNumber = pickRow(row, "RegNumber", "regNumber");
          return rowTeacherId
            ? String(rowTeacherId) === String(teacherId)
            : String(rowRegNumber) === String(selectedTeacher.regNumber);
        });
        const assignment = exactMatch;
        if (!assignment) return null;

        return {
          establishmentId: pickRow(
            assignment,
            "EstabId",
            "estabId",
            "EstablishmentId",
            "establishmentId"
          ),
          establishmentName: pickRow(
            assignment,
            "EstName",
            "estName",
            "EstablishmentName",
            "establishmentName",
            "establishment"
          ),
          teacherName:
            [
              pickRow(assignment, "Name", "name", "FirstName", "firstName"),
              pickRow(assignment, "PreName", "preName", "LastName", "lastName"),
            ]
              .filter(Boolean)
              .join(" ") || [selectedTeacher.name, selectedTeacher.preName].filter(Boolean).join(" "),
        };
      } catch (error) {
        console.warn("Unable to check the teacher's current assignment", error);
        return null;
      }
    },
    [teacherOpts]
  );

  const formik = useFormik({
    initialValues: {
      province: "",
      department: "",
      subprefecture: "",
      type: "",
      status: "",
      establishment: "",
      teachers: "",
      date: "",
    },
    validationSchema: Yup.object({
      province: Yup.string().required(t("validation.province_required", "Province est requis")),
      department: Yup.string().required(t("validation.department_required", "Département est requis")),
      subprefecture: Yup.string().required(t("validation.subprefecture_required", "Sous-préfecture est requis")),
      establishment: Yup.string().required(t("validation.establishment_required", "Établissement est requis")),
      teachers: Yup.string().required(t("validation.teachers_required", "Enseignants à affecter est requis")),
      date: Yup.string().required(t("validation.assignment_date_required", "Date de l'affectation est requis")),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      let assignmentCreated = false;
      try {
        const existingAssignment = await findExistingTeacherAssignment(values.teachers);
        const targetEstablishment = estabOpts.find(
          (option) => String(option.id) === String(values.establishment)
        );
        const existingId = String(existingAssignment?.establishmentId ?? "").trim();
        const existingName = String(existingAssignment?.establishmentName ?? "").trim();
        const targetName = String(targetEstablishment?.text ?? "").trim();
        const isDifferentEstablishment = existingAssignment && (
          existingId
            ? existingId !== String(values.establishment)
            : existingName && targetName && existingName.toLowerCase() !== targetName.toLowerCase()
        );

        let forceReassignment = false;
        if (isDifferentEstablishment) {
          const confirmation = await Swal.fire({
            ...swalApp,
            icon: "warning",
            title: t("assignments.reassign_confirm_title", "Teacher already assigned"),
            text: t(
              "assignments.reassign_confirm_text",
              "{{teacher}} is currently assigned to {{establishment}}. Assign them to the new establishment instead?"
            )
              .replace("{{teacher}}", existingAssignment.teacherName || t("assignments.teacher_fallback", "This teacher"))
              .replace("{{establishment}}", existingName || t("assignments.establishment_fallback", "another establishment")),
            showCancelButton: true,
            reverseButtons: true,
            confirmButtonText: t("assignments.reassign_confirm", "Yes, reassign"),
            cancelButtonText: t("assignments.reassign_cancel", "Cancel"),
          });
          if (!confirmation.isConfirmed) return;
          forceReassignment = true;
        }

        setLoading(true);
        const formData = new FormData();
        buildAssignmentCreationEntries(values, forceReassignment).forEach(([key, value]) => {
          formData.append(key, value);
        });

        const response = await axios.post(`${apiurl}Assignment/addassignment`, formData, {
          headers: authHeader(),
        });
        if (response.data === true) {
          Swal.fire({
            ...swalApp,
            icon: "success",
            title: t("assignments.create_success_title", "Affectation créée"),
            text: t("assignments.create_success_text", "L'affectation a été créée avec succès."),
            showConfirmButton: false,
            timer: 2000,
          });
          await fetchAssignmentSummary();
          assignmentCreated = true;
        }
      } catch (error) {
        console.error("Error creating assignment:", error);
        Swal.fire({
          ...swalApp,
          icon: "error",
          title: t("assignments.create_error_title", "Erreur"),
          text: t("assignments.create_error_text", "Une erreur s'est produite lors de la création de l'affectation."),
        });
      } finally {
        setLoading(false);
        setSubmitting(false);
        if (assignmentCreated) resetForm();
      }
    },
  });

  const assignmentEstablishmentFilterValues = useMemo(
    () => deriveEstablishmentFilterValues(assignmentEstablishmentRows),
    [assignmentEstablishmentRows]
  );
  const assignmentFormTypeOptions = useMemo(
    () => establishmentTypeOptions(t, assignmentEstablishmentFilterValues.types),
    [t, assignmentEstablishmentFilterValues.types]
  );
  const assignmentFormStatusOptions = useMemo(
    () => establishmentStatusOptions(t, assignmentEstablishmentFilterValues.statuses),
    [t, assignmentEstablishmentFilterValues.statuses]
  );

  const applyAssignmentEstablishmentFilters = (type, status) => {
    const rows = filterEstablishmentRows(assignmentEstablishmentRows, { type, status });
    setEstabOpts(
      makeOptions(
        rows,
        ["id", "Id", "estabId", "EstabId", "establishmentId", "EstablishmentId"],
        ["name", "establishment", "Name", "EstablishmentName"]
      )
    );
  };

  const clearAssignmentDropdowns = () => {
    formik.setValues({
      ...formik.values,
      province: "",
      department: "",
      subprefecture: "",
      type: "",
      status: "",
      establishment: "",
      teachers: "",
    });
    formik.setTouched(
      {
        ...formik.touched,
        province: false,
        department: false,
        subprefecture: false,
        establishment: false,
        teachers: false,
      },
      false
    );
    setDeptOpts([]);
    setSubpOpts([]);
    setEstabOpts([]);
    setAssignmentEstablishmentRows([]);
    setTeacherOpts([]);
    setTeacherAssignmentFilter("all");
  };

  const appliedFilterCount = useMemo(
    () => Object.values(appliedListFilters).filter((v) => String(v ?? "").trim()).length,
    [appliedListFilters]
  );

  const listFilterActive = appliedFilterCount > 0;

  const updateDraftListFilter = (name, value) => {
    setDraftListFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyListFilters = () => {
    setAppliedListFilters({ ...draftListFilters });
    setPage(1);
  };

  const resetListFilters = () => {
    setDraftListFilters(EMPTY_ASSIGNMENT_FILTERS);
    setAppliedListFilters(EMPTY_ASSIGNMENT_FILTERS);
    setFilterDeptOpts([]);
    setFilterSubpOpts([]);
    setFilterEstabOpts([]);
    setPage(1);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") handleApplyListFilters();
  };

  const labelForOption = (opts, id) => opts.find((o) => String(o.id) === String(id))?.text;

  const assistantPageSnapshot = useMemo(() => {
    return {
      pageKey: "affectations",
      route: "/affectations",
      filters: {
        page,
        totalRecords,
        appliedListFilters,
        activeApiFilters,
        province: labelForOption(provOpts, appliedListFilters.provinceId),
        department: labelForOption(filterDeptOpts, appliedListFilters.departmentId),
        subprefecture: labelForOption(filterSubpOpts, appliedListFilters.subPrefectureId),
        establishment: labelForOption(filterEstabOpts, appliedListFilters.establishmentId),
      },
    };
  }, [
    page,
    totalRecords,
    appliedListFilters,
    activeApiFilters,
    provOpts,
    filterDeptOpts,
    filterSubpOpts,
    filterEstabOpts,
  ]);
  useRegisterAssistantPage(assistantPageSnapshot);

  const totalPages = totalPagesFromCount(totalRecords, pageSize);

  const assignmentTableRangeLabel =
    totalRecords === 0
      ? null
      : t("assignments.table_range", "{{start}}–{{end}} of {{total}} rows")
          .replace("{{start}}", String((page - 1) * pageSize + 1))
          .replace("{{end}}", String(Math.min(page * pageSize, totalRecords)))
          .replace("{{total}}", String(totalRecords));

  const statCards = [
    {
      key: "total",
      title: t("assignments.stats.total", "Records"),
      count: assignmentApiSummary.totalRecords,
      icon: "assignment",
      color: "info",
    },
    {
      key: "month",
      title: t("assignments.stats.this_month", "This month"),
      count: assignmentApiSummary.thisMonthTotal,
      icon: "calendar_month",
      color: "secondary",
    },
    {
      key: "today",
      title: t("assignments.stats.today", "Today"),
      count: assignmentApiSummary.todayTotal,
      icon: "today",
      color: "success",
    },
    {
      key: "week",
      title: t("assignments.stats.this_week", "This week"),
      count: assignmentApiSummary.thisWeekTotal,
      icon: "date_range",
      color: "warning",
    },
    {
      key: "establishments",
      title: t("assignments.stats.establishments", "Establishments"),
      count: assignmentApiSummary.totalEstablishments,
      icon: "business",
      color: "dark",
    },
  ];

  return (
    <DashboardLayout>
      {loading && <Loading />}
      <DashboardNavbar />
      <SoftBox py={3} sx={{ bgcolor: figmaTokens.pageBg }} className="content-area-main">
        <SoftBox mb={3}>
          <Card
            sx={{
              borderRadius: "18px",
              overflow: "hidden",
              border: "none",
              boxShadow: "0 22px 56px rgba(15, 23, 42, 0.09)",
            }}
          >
            <SoftBox sx={{ height: 5, background: figmaTokens.gradientCSS }} />
            <SoftBox sx={{ p: { xs: 2.25, md: 3 } }}>
              <SoftTypography
                variant="overline"
                sx={{
                  fontWeight: 800,
                  letterSpacing: "0.14em",
                  color: figmaTokens.textMuted,
                  fontSize: "0.68rem",
                }}
              >
                {t("assignments.page_kicker", "ASSIGNMENTS")}
              </SoftTypography>
              <SoftTypography variant="h4" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, mt: 0.5 }}>
                {t("assignments.title", "Affectation des enseignants")}
              </SoftTypography>
              <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, mt: 1, maxWidth: 640 }}>
                {t(
                  "assignments.page_hint",
                  "Link teachers to establishments and review history."
                )}
              </SoftTypography>
              <SoftTypography variant="caption" sx={{ display: "block", mt: 1, color: figmaTokens.textMuted }}>
                {t("assignments.stats.page_hint", "Filtered results")}
              </SoftTypography>
            </SoftBox>
          </Card>
        </SoftBox>

        <SoftBox
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(3, minmax(0, 1fr))",
              lg: "repeat(5, minmax(0, 1fr))",
            },
            mb: 3,
          }}
        >
          {statCards.map((card) => (
            <SoftBox key={card.key} sx={assignmentStatWrapSx}>
              <MiniStatisticsCard
                title={{ text: card.title }}
                count={card.count}
                percentage={{ color: "success", text: "" }}
                icon={{ color: card.color, component: card.icon }}
              />
            </SoftBox>
          ))}
        </SoftBox>

        {!readOnly && <Card
          variant="outlined"
          sx={{
            mb: 3,
            borderRadius: "16px",
            border: `1px solid ${figmaTokens.borderSubtle}`,
            boxShadow: "0 14px 44px rgba(15, 23, 42, 0.07)",
            bgcolor: figmaTokens.surface,
            overflow: "hidden",
          }}
        >
          <SoftBox
            sx={{
              px: { xs: 2, md: 2.5 },
              py: 2,
              borderBottom: `1px solid ${figmaTokens.cardHeaderRule}`,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <SoftTypography variant="h6" fontWeight="bold" sx={{ color: figmaTokens.textPrimary }}>
              {t("assignments.form_section_title", "New assignment")}
            </SoftTypography>
            <Button
              variant={formOpen ? "outlined" : "contained"}
              disableElevation={!formOpen}
              onClick={() => setFormOpen((prev) => !prev)}
              sx={
                formOpen
                  ? {
                      textTransform: "none",
                      fontWeight: 700,
                      px: 2.5,
                      py: 1,
                      borderRadius: "12px",
                      borderColor: figmaTokens.borderStrong,
                      color: figmaTokens.textSecondary,
                    }
                  : {
                      textTransform: "none",
                      fontWeight: 700,
                      px: 2.75,
                      py: 1,
                      borderRadius: "12px",
                      color: "#fff",
                      background: figmaTokens.gradientCSS,
                      boxShadow: "0 10px 26px rgba(102, 126, 234, 0.35)",
                      "&:hover": {
                        filter: "brightness(1.06)",
                        boxShadow: "0 14px 32px rgba(102, 126, 234, 0.42)",
                      },
                    }
              }
            >
              {formOpen
                ? t("assignments.form_hide", "Hide form")
                : t("assignments.form_add", "Add assignment")}
            </Button>
          </SoftBox>
          <Collapse in={formOpen} timeout="auto" unmountOnExit>
            <form onSubmit={formik.handleSubmit} className="assignment-form">
              <Grid container spacing={1} sx={{ p: 2 }}>
                <Grid item xs={12} xl={3}>
                  <label>{t("assignments.province", "Province")}</label>
                  <Select2Field
                    name="province"
                    options={[
                      { id: "", text: t("assignments.province_placeholder", "Sélectionner province") },
                      ...provOpts,
                    ]}
                    placeholder={t("assignments.province_placeholder", "Sélectionner province")}
                    value={formik.values.province}
                    allowClear
                    onChange={(provid) => {
                      formik.setFieldValue("province", provid);
                      formik.setFieldValue("department", "");
                      formik.setFieldValue("subprefecture", "");
                      formik.setFieldValue("type", "");
                      formik.setFieldValue("status", "");
                      formik.setFieldValue("establishment", "");
                      setDeptOpts([]);
                      setSubpOpts([]);
                      setEstabOpts([]);
                      setAssignmentEstablishmentRows([]);
                      fetchDepartments(provid);
                    }}
                  />
                  {formik.errors.province && formik.touched.province && (
                    <div style={{ color: "red", fontSize: 12 }}>{formik.errors.province}</div>
                  )}
                </Grid>
                <Grid item xs={12} xl={3}>
                  <label>{t("assignments.department", "Département")}</label>
                  <Select2Field
                    name="department"
                    options={[
                      { id: "", text: t("assignments.department_placeholder", "Sélectionner Département") },
                      ...deptOpts,
                    ]}
                    placeholder={t("assignments.department_placeholder", "Sélectionner Département")}
                    disabled={!formik.values.province}
                    value={formik.values.department}
                    allowClear
                    onChange={(dptid) => {
                      formik.setFieldValue("department", dptid);
                      formik.setFieldValue("subprefecture", "");
                      formik.setFieldValue("type", "");
                      formik.setFieldValue("status", "");
                      formik.setFieldValue("establishment", "");
                      setSubpOpts([]);
                      setEstabOpts([]);
                      setAssignmentEstablishmentRows([]);
                      fetchSubprefectures(dptid);
                    }}
                  />
                  {formik.errors.department && formik.touched.department && (
                    <div style={{ color: "red", fontSize: 12 }}>{formik.errors.department}</div>
                  )}
                </Grid>
                <Grid item xs={12} xl={3}>
                  <label>{t("assignments.subprefecture", "Sous-préfecture")}</label>
                  <Select2Field
                    name="subprefecture"
                    options={[
                      { id: "", text: t("assignments.subprefecture_placeholder", "Sélectionner Sous-préfecture") },
                      ...subpOpts,
                    ]}
                    placeholder={t("assignments.subprefecture_placeholder", "Sélectionner Sous-préfecture")}
                    disabled={!formik.values.department}
                    value={formik.values.subprefecture}
                    allowClear
                    onChange={(sptid) => {
                      formik.setFieldValue("subprefecture", sptid);
                      formik.setFieldValue("type", "");
                      formik.setFieldValue("status", "");
                      formik.setFieldValue("establishment", "");
                      setEstabOpts([]);
                      setAssignmentEstablishmentRows([]);
                      fetchEstablishments(sptid);
                    }}
                  />
                  {formik.errors.subprefecture && formik.touched.subprefecture && (
                    <div style={{ color: "red", fontSize: 12 }}>{formik.errors.subprefecture}</div>
                  )}
                </Grid>
                <Grid item xs={12} xl={3}>
                  <label>{t("establishments.col.type", "Type")}</label>
                  <Select2Field
                    name="assignmentEstablishmentType"
                    options={assignmentFormTypeOptions}
                    value={formik.values.type}
                    disabled={!formik.values.subprefecture}
                    allowClear
                    onChange={(value) => {
                      formik.setFieldValue("type", value);
                      formik.setFieldValue("establishment", "");
                      applyAssignmentEstablishmentFilters(value, formik.values.status);
                    }}
                  />
                </Grid>
                <Grid item xs={12} xl={3}>
                  <label>{t("establishments.col.status", "Status")}</label>
                  <Select2Field
                    name="assignmentEstablishmentStatus"
                    options={assignmentFormStatusOptions}
                    value={formik.values.status}
                    disabled={!formik.values.subprefecture}
                    allowClear
                    onChange={(value) => {
                      formik.setFieldValue("status", value);
                      formik.setFieldValue("establishment", "");
                      applyAssignmentEstablishmentFilters(formik.values.type, value);
                    }}
                  />
                </Grid>
                <Grid item xs={12} xl={3}>
                  <label>{t("assignments.establishment", "Établissement")}</label>
                  <Select2Field
                    name="establishment"
                    options={[
                      { id: "", text: t("assignments.establishment_placeholder", "Sélectionner Établissement") },
                      ...estabOpts,
                    ]}
                    placeholder={t("assignments.establishment_placeholder", "Sélectionner Établissement")}
                    disabled={!formik.values.subprefecture}
                    value={formik.values.establishment}
                    allowClear
                    onChange={(val) => formik.setFieldValue("establishment", val)}
                  />
                  {formik.errors.establishment && formik.touched.establishment && (
                    <div style={{ color: "red", fontSize: 12 }}>{formik.errors.establishment}</div>
                  )}
                </Grid>
                <Grid item xs={12} xl={3}>
                  <label>{t("assignments.teachers", "Enseignants à affecter")}</label>
                  <SoftBox
                    display="flex"
                    gap={0.5}
                    mt={0.5}
                    mb={1}
                    p={0.5}
                    sx={{
                      bgcolor: figmaTokens.pageBg,
                      border: `1px solid ${figmaTokens.borderSubtle}`,
                      borderRadius: "0.65rem",
                    }}
                  >
                    {[
                      ["all", t("assignments.teacher_filter.all", "All")],
                      ["assigned", t("assignments.teacher_filter.assigned", "Assigned")],
                      ["unassigned", t("assignments.teacher_filter.unassigned", "Unassigned")],
                    ].map(([status, label]) => {
                      const selected = teacherAssignmentFilter === status;
                      return (
                        <Button
                          key={status}
                          type="button"
                          size="small"
                          variant="outlined"
                          aria-pressed={selected}
                          onClick={() => setTeacherAssignmentFilter(status)}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            px: 0.75,
                            py: 0.6,
                            borderRadius: "0.5rem",
                            borderColor: selected
                              ? "transparent !important"
                              : `${figmaTokens.borderStrong} !important`,
                            bgcolor: selected ? figmaTokens.accent : figmaTokens.surface,
                            backgroundImage: selected ? figmaTokens.gradientCSS : "none",
                            color: selected
                              ? "#ffffff !important"
                              : `${figmaTokens.textPrimary} !important`,
                            boxShadow: selected ? "0 3px 8px rgba(102, 126, 234, 0.28)" : "none",
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            lineHeight: 1.2,
                            textTransform: "none",
                            transition: "all 0.16s ease",
                            "&:hover": {
                              borderColor: `${figmaTokens.accent} !important`,
                              bgcolor: selected ? figmaTokens.accent : "#eef2ff",
                              backgroundImage: selected ? figmaTokens.gradientCSS : "none",
                              color: selected
                                ? "#ffffff !important"
                                : `${figmaTokens.accentEnd} !important`,
                            },
                            "&:focus-visible": {
                              outline: `3px solid ${figmaTokens.focusRing}`,
                              outlineOffset: 1,
                            },
                          }}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </SoftBox>
                  <Select2Field
                    key={teacherAssignmentFilter}
                    name="teachers"
                    options={[
                      { id: "", text: t("assignments.teachers_placeholder", "Sélectionner Enseignant") },
                      ...teacherOpts,
                    ]}
                    placeholder={t("assignments.teachers_placeholder", "Sélectionner Enseignant")}
                    value={formik.values.teachers}
                    allowClear
                    onChange={(val) => {
                      formik.setFieldValue("teachers", val);
                      const selected = teacherOptionCacheRef.current.get(String(val));
                      setTeacherOpts(selected ? [selected] : []);
                    }}
                    showInitials
                    loadOptions={loadTeacherOptions}
                    asyncPageSize={ASSIGNMENT_TEACHER_PAGE_SIZE}
                  />
                  {formik.errors.teachers && formik.touched.teachers && (
                    <div style={{ color: "red", fontSize: 12 }}>{formik.errors.teachers}</div>
                  )}
                </Grid>
                <Grid item xs={12} xl={3}>
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    {t("assignments.date", "Date de l'affectation")}
                  </SoftTypography>
                  <SoftInput
                    type="date"
                    name="date"
                    value={formik.values.date}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.date && formik.errors.date ? (
                    <div className="error-message">{formik.errors.date}</div>
                  ) : null}
                </Grid>
                <Grid item xs={12} xl={3}>
                  <SoftButton
                    type="button"
                    variant="outlined"
                    color="dark"
                    fullWidth
                    style={{ marginTop: "30px" }}
                    onClick={clearAssignmentDropdowns}
                    disabled={![
                      formik.values.province,
                      formik.values.department,
                      formik.values.subprefecture,
                      formik.values.type,
                      formik.values.status,
                      formik.values.establishment,
                      formik.values.teachers,
                    ].some(Boolean)}
                  >
                    {t("assignments.clear_selections", "EFFACER LES SÉLECTIONS")}
                  </SoftButton>
                </Grid>
                <Grid item xs={12} xl={3}>
                  <SoftButton type="submit" variant="gradient" color="info" fullWidth style={{ marginTop: "30px" }}>
                    {formik.isSubmitting ? "Loading..." : t("assignments.save", "ENREGISTRER")}
                  </SoftButton>
                </Grid>
              </Grid>
            </form>
          </Collapse>
        </Card>}

        <Card
          variant="outlined"
          sx={{
            borderRadius: "16px",
            border: `1px solid ${figmaTokens.borderSubtle}`,
            boxShadow: "0 14px 44px rgba(15, 23, 42, 0.07)",
            overflow: "hidden",
            bgcolor: figmaTokens.surface,
          }}
        >
          <SoftBox
            sx={{
              px: { xs: 2, md: 2.5 },
              py: 2,
              borderBottom: `1px solid ${figmaTokens.cardHeaderRule}`,
            }}
          >
            <SoftTypography variant="h6" fontWeight="bold" sx={{ color: figmaTokens.textPrimary }}>
              {t("assignments.summary_title", "Récapitulatif des affectations")}
            </SoftTypography>
          </SoftBox>

          <SoftBox sx={{ px: { xs: 1.25, md: 2 }, pt: 2, pb: 2 }}>
            {listFilterActive && (
              <SoftBox
                sx={{
                  px: 2,
                  py: 1.25,
                  mb: 1.5,
                  borderRadius: "12px",
                  bgcolor: "rgba(102, 126, 234, 0.07)",
                  border: "1px solid rgba(102, 126, 234, 0.18)",
                }}
              >
                <SoftTypography variant="body2" sx={{ fontWeight: 700, color: figmaTokens.textPrimary }}>
                  {t("assignments.viewing_filtered", "{{total}} assignment(s) matching filters").replace(
                    "{{total}}",
                    String(totalRecords)
                  )}
                </SoftTypography>
              </SoftBox>
            )}

            <SoftBox
              sx={{
                mb: 1.5,
                borderRadius: "12px",
                border: `1px solid ${figmaTokens.borderSubtle}`,
                bgcolor: figmaTokens.pageBg,
                overflow: "hidden",
              }}
            >
              <SoftBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                px={1.5}
                py={1.25}
                sx={{ borderBottom: filtersMinimized ? "none" : `1px solid ${figmaTokens.borderSubtle}` }}
              >
                <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <SoftTypography variant="caption" fontWeight="bold">
                    {t("assignments.server_filters", "Search & filters")}
                  </SoftTypography>
                  {listFilterActive && (
                    <Chip
                      size="small"
                      label={t("assignments.filters_active_count", "{{count}} active").replace(
                        "{{count}}",
                        String(appliedFilterCount)
                      )}
                      sx={{ height: 22, fontSize: "0.65rem", fontWeight: 700 }}
                    />
                  )}
                </SoftBox>
                <Tooltip
                  title={
                    filtersMinimized
                      ? t("teachers.filters_expand", "Show filters")
                      : t("teachers.filters_minimize", "Minimize filters")
                  }
                >
                  <IconButton
                    size="small"
                    onClick={() => setFiltersMinimized((v) => !v)}
                    sx={{
                      color: figmaTokens.accent,
                      border: `1px solid ${figmaTokens.borderSubtle}`,
                      borderRadius: "10px",
                    }}
                  >
                    {filtersMinimized ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </SoftBox>

              <Collapse in={!filtersMinimized} timeout="auto">
                <SoftBox p={1.5}>
                  <SoftBox sx={{ position: "relative", mb: 1.5 }}>
                    <SearchOutlined
                      sx={{
                        position: "absolute",
                        left: 12,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: "1.1rem",
                        color: figmaTokens.textMuted,
                        pointerEvents: "none",
                      }}
                    />
                    <SoftInput
                      type="search"
                      value={draftListFilters.search}
                      onChange={(e) => updateDraftListFilter("search", e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      placeholder={t(
                        "assignments.search_placeholder",
                        "Search reg number, name, phone, establishment…"
                      )}
                      sx={{ "& input": { pl: "2.5rem" } }}
                    />
                  </SoftBox>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6} md={3}>
                      <SoftTypography component="label" sx={filterLabelSx}>
                        {t("establishments.col.type", "Type")}
                      </SoftTypography>
                      <Select2Field
                        name="filterAssignmentType"
                        options={establishmentTypeFilterOptions}
                        value={draftListFilters.type}
                        onChange={(value) => {
                          updateDraftListFilter("type", value);
                          if (draftListFilters.subPrefectureId) {
                            fetchFilterEstablishments(draftListFilters.subPrefectureId, {
                              type: value,
                              status: draftListFilters.status,
                            });
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <SoftTypography component="label" sx={filterLabelSx}>
                        {t("teachers.reg_number", "Matricule")}
                      </SoftTypography>
                      <SoftInput
                        value={draftListFilters.regNumber}
                        onChange={(e) => updateDraftListFilter("regNumber", e.target.value)}
                        placeholder={t("teachers.reg_number", "Matricule")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <SoftTypography component="label" sx={filterLabelSx}>
                        {t("establishments.col.status", "Status")}
                      </SoftTypography>
                      <Select2Field
                        name="filterAssignmentStatus"
                        options={establishmentStatusFilterOptions}
                        value={draftListFilters.status}
                        onChange={(value) => {
                          updateDraftListFilter("status", value);
                          if (draftListFilters.subPrefectureId) {
                            fetchFilterEstablishments(draftListFilters.subPrefectureId, {
                              type: draftListFilters.type,
                              status: value,
                            });
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <SoftTypography component="label" sx={filterLabelSx}>
                        {t("assignments.province", "Province")}
                      </SoftTypography>
                      <Select2Field
                        name="filterProvince"
                        options={[
                          { id: "", text: t("assignments.province_placeholder", "Select province") },
                          ...provOpts,
                        ]}
                        value={draftListFilters.provinceId}
                        onChange={(provid) => {
                          setDraftListFilters((prev) => ({
                            ...prev,
                            provinceId: provid,
                            departmentId: "",
                            subPrefectureId: "",
                            establishmentId: "",
                            type: "",
                            status: "",
                          }));
                          setFilterDeptOpts([]);
                          setFilterSubpOpts([]);
                          setFilterEstabOpts([]);
                          fetchFilterDepartments(provid);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <SoftTypography component="label" sx={filterLabelSx}>
                        {t("assignments.department", "Department")}
                      </SoftTypography>
                      <Select2Field
                        name="filterDepartment"
                        options={[
                          { id: "", text: t("assignments.department_placeholder", "Select department") },
                          ...filterDeptOpts,
                        ]}
                        disabled={!draftListFilters.provinceId}
                        value={draftListFilters.departmentId}
                        onChange={(dptid) => {
                          setDraftListFilters((prev) => ({
                            ...prev,
                            departmentId: dptid,
                            subPrefectureId: "",
                            establishmentId: "",
                            type: "",
                            status: "",
                          }));
                          setFilterSubpOpts([]);
                          setFilterEstabOpts([]);
                          fetchFilterSubprefectures(dptid);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <SoftTypography component="label" sx={filterLabelSx}>
                        {t("assignments.subprefecture", "Sub-prefecture")}
                      </SoftTypography>
                      <Select2Field
                        name="filterSubprefecture"
                        options={[
                          { id: "", text: t("assignments.subprefecture_placeholder", "Select sub-prefecture") },
                          ...filterSubpOpts,
                        ]}
                        disabled={!draftListFilters.departmentId}
                        value={draftListFilters.subPrefectureId}
                        onChange={(sptid) => {
                          setDraftListFilters((prev) => ({
                            ...prev,
                            subPrefectureId: sptid,
                            establishmentId: "",
                            type: "",
                            status: "",
                          }));
                          setFilterEstabOpts([]);
                          fetchFilterEstablishments(sptid);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <SoftTypography component="label" sx={filterLabelSx}>
                        {t("assignments.establishment", "Establishment")}
                      </SoftTypography>
                      <Select2Field
                        name="filterEstablishment"
                        options={[
                          { id: "", text: t("assignments.establishment_placeholder", "Select establishment") },
                          ...filterEstabOpts,
                        ]}
                        disabled={!draftListFilters.subPrefectureId}
                        value={draftListFilters.establishmentId}
                        onChange={(val) => updateDraftListFilter("establishmentId", val)}
                      />
                    </Grid>
                  </Grid>

                  <SoftBox display="flex" gap={1} flexWrap="wrap" mt={1.5}>
                    <SoftButton variant="gradient" color="info" onClick={handleApplyListFilters}>
                      {t("teacher_license.apply", "Apply")}
                    </SoftButton>
                    <SoftButton
                      variant="outlined"
                      color="secondary"
                      onClick={resetListFilters}
                      disabled={!listFilterActive}
                    >
                      {t("teacher_license.reset", "Reset")}
                    </SoftButton>
                  </SoftBox>
                  <SoftTypography variant="caption" sx={{ display: "block", mt: 1, color: figmaTokens.textMuted }}>
                    {t(
                      "assignments.server_filters_hint",
                      "Filters run on the server. Dashboard cards use top-level Stats from the API."
                    )}
                  </SoftTypography>
                </SoftBox>
              </Collapse>
            </SoftBox>

            {assignmentTableRangeLabel && (
              <SoftTypography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1.5,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  color: figmaTokens.textMuted,
                  textTransform: "uppercase",
                }}
              >
                {assignmentTableRangeLabel}
              </SoftTypography>
            )}

            <TableContainer component={Paper} elevation={0} sx={assignmentTableShellSx}>
              <Table
                size="small"
                stickyHeader
                sx={{
                  minWidth: 1200,
                  ...directoryTableHeadStickyOverridesSx,
                  ...directoryTableBodyCellDefaultsSx,
                  "& .MuiTableRow-root:hover .MuiTableCell-root": {
                    bgcolor: "rgba(118, 75, 162, 0.045)",
                  },
                }}
                aria-label={t("assignments.summary_title", "Récapitulatif des affectations")}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 168 }}>
                      {t("assignments.table.date", "Date de l'affectation")}
                    </TableCell>
                    <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 120 }}>
                      {t("teachers.table.reg", "Matricule de l'enseignant")}
                    </TableCell>
                    <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 180 }}>
                      {t("assignments.table.teacher_name", "Nom et prénoms de l'enseignant")}
                    </TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>{t("teachers.table.gender", "Sexe de l'enseignant")}</TableCell>
                    <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 120 }}>
                      {t("teachers.table.status", "Statut de l'enseignant")}
                    </TableCell>
                    <TableCell sx={directoryTableHeaderCellSx}>
                      {t("assignments.table.teacher_degree", "Diplôme de l'enseignant")}
                    </TableCell>
                    <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 160 }}>
                      {t("statistics.section.establishment", "Établissement")}
                    </TableCell>
                    <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 100 }}>
                      {t("establishments.table.code", "Code")}
                    </TableCell>
                    <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 140 }}>
                      {t("assignments.table.subprefecture", "Sous-préfecture")}
                    </TableCell>
                    <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 140 }}>
                      {t("assignments.table.department", "Département")}
                    </TableCell>
                    <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 120 }}>
                      {t("assignments.table.province", "Province")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignmentSummary.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={11}
                        align="center"
                        sx={{ py: 6, color: figmaTokens.textSecondary, fontSize: "0.9rem" }}
                      >
                        {t("assignments.table_empty", "No assignments to display.")}
                      </TableCell>
                    </TableRow>
                  )}
                  {assignmentSummary.map((row, index) => {
                    const teacherStatus = assignmentTeacherStatus(row);
                    const regNumber = pickRow(row, "RegNumber", "regNumber");
                    const firstName = pickRow(row, "Name", "name");
                    const lastName = pickRow(row, "PreName", "preName");
                    const fullName = [firstName, lastName].filter(Boolean).join(" ");
                    const estName = pickRow(row, "EstName", "estName", "establishment");
                    const estCode = pickRow(row, "Code", "code");
                    const rowKey = `${regNumber || "row"}-${pickRow(row, "AssigDateTime", "assigDateTime")}-${index}`;

                    return (
                      <TableRow
                        key={rowKey}
                        hover
                        sx={{
                          "&:nth-of-type(even) td": { bgcolor: "rgba(247, 249, 252, 0.65)" },
                          transition: "background-color 0.15s ease",
                        }}
                      >
                        <TableCell sx={{ fontWeight: 700, color: figmaTokens.textPrimary, whiteSpace: "nowrap" }}>
                          {formatAssignmentDate(pickRow(row, "AssigDateTime", "assigDateTime"))}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{regNumber || "--"}</TableCell>
                        <TableCell
                          sx={{ maxWidth: 280, fontWeight: 600 }}
                          title={fullName}
                        >
                          <SoftBox display="flex" alignItems="center" gap={1.25} minWidth={0}>
                            <Avatar
                              aria-hidden="true"
                              sx={{
                                width: 30,
                                height: 30,
                                bgcolor: "rgba(118, 75, 162, 0.12)",
                                color: figmaTokens.accent,
                                fontSize: "0.72rem",
                                fontWeight: 700,
                              }}
                            >
                              {teacherInitials(fullName)}
                            </Avatar>
                            <SoftTypography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {fullName || "--"}
                            </SoftTypography>
                          </SoftBox>
                        </TableCell>
                        <TableCell sx={{ color: figmaTokens.textBody }}>
                          {pickRow(row, "Gender", "gender") || "--"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={teacherStatus || "--"}
                            size="small"
                            sx={assignmentStatusChipSx(teacherStatus)}
                          />
                        </TableCell>
                        <TableCell>{pickRow(row, "Degree", "degree") || "--"}</TableCell>
                        <TableCell sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }} title={estName}>
                          {estName || "--"}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{estCode || "--"}</TableCell>
                        <TableCell sx={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }} title={pickRow(row, "SPName", "spName")}>
                          {pickRow(row, "SPName", "spName") || "--"}
                        </TableCell>
                        <TableCell
                          sx={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}
                          title={pickRow(row, "DistrictName", "districtName")}
                        >
                          {pickRow(row, "DistrictName", "districtName") || "--"}
                        </TableCell>
                        <TableCell
                          sx={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }}
                          title={pickRow(row, "ProvinceName", "provinceName")}
                        >
                          {pickRow(row, "ProvinceName", "provinceName") || "--"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePaginationBar
              page={page}
              totalCount={totalRecords}
              pageSize={pageSize}
              pageSizeOptions={[10, 20, 50]}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={(nextSize) => {
                setPageSize(nextSize);
                setPage(1);
              }}
              pageSizeLabel={t("pagination.page_size", "Rows per page")}
            />
          </SoftBox>
        </Card>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Affectations;
